import { buildCoarseLocationText } from './locationPrivacy';
import { notifySuccessToast } from './notifyActionError';

/**
 * Native sharing utility using Web Share API with fallbacks
 * @param {Object} shareData - The data to share
 * @param {string} shareData.title - Title for the share
 * @param {string} shareData.text - Text content to share
 * @param {string} shareData.url - URL to share (optional, defaults to current URL)
 * @param {Object} options - Additional options
 * @param {boolean} options.showAlert - Show alert on clipboard copy (default: true)
 * @param {string} options.alertMessage - Custom alert message
 * @returns {Promise<boolean>} - Returns true if sharing was successful
 */
export const shareContent = async (shareData, options = {}) => {
  const {
    showAlert = true,
    alertMessage = 'Link copied to clipboard!'
  } = options;

  // Ensure we have required data
  if (!shareData.title && !shareData.text) {
    console.error('shareContent: title or text is required');
    return false;
  }

  // Use current URL if none provided
  const finalShareData = {
    ...shareData,
    url: shareData.url || window.location.href
  };

  // Check if Web Share API is supported
  if (navigator.share) {
    try {
      await navigator.share(finalShareData);
      return true;
    } catch (error) {
      // User cancelled sharing or sharing failed
      return false;
    }
  } else {
    // Fallback for browsers that don't support Web Share API
    try {
      const fallbackText = `${finalShareData.text || finalShareData.title}\n${finalShareData.url}`;
      await navigator.clipboard.writeText(fallbackText);
      
      if (showAlert) {
        notifySuccessToast(alertMessage, 'Link kopiert');
      }
      
      return true;
    } catch (error) {
      // Final fallback using prompt
      const fallbackText = `${finalShareData.text || finalShareData.title}\n${finalShareData.url}`;
      prompt('Copy this link to share:', fallbackText);
      return true;
    }
  }
};

/**
 * Create booking share data
 * @param {Object} bookingData - Booking data object
 * @param {Object} options - Custom options
 * @returns {Object} - Share data object
 */
export const createBookingShareData = (bookingData, options = {}) => {
  if (!bookingData || !bookingData.Studio) {
    return null;
  }

  const studioName = bookingData.Studio.StudioName;
  const projectName = bookingData.projectName;
  
  return {
    title: options.title || `Booking at ${studioName}`,
    text: options.text || `Check out my booking at ${studioName}${projectName ? ` for "${projectName}"` : ''}`,
    url: options.url || window.location.href
  };
};

/**
 * Create studio share data
 * @param {Object} studioData - Studio data object
 * @param {Object} options - Custom options
 * @returns {Object} - Share data object
 */
export const createStudioShareData = (studioData, options = {}) => {
  if (!studioData || !studioData.StudioName) {
    return null;
  }

  const studioName = studioData.StudioName;
  const location = buildCoarseLocationText(studioData);
  
  return {
    title: options.title || `${studioName} Studio`,
    text: options.text || `Check out this amazing studio: ${studioName}${location ? ` in ${location}` : ''}`,
    url: options.url || window.location.href
  };
};

/**
 * Quick share function for booking details
 * @param {Object} bookingData - Booking data object
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - Returns true if sharing was successful
 */
export const shareBooking = async (bookingData, options = {}) => {
  const shareData = createBookingShareData(bookingData, options);
  if (!shareData) {
    console.error('shareBooking: Invalid booking data');
    return false;
  }
  
  return await shareContent(shareData, options);
};

/**
 * Quick share function for studio details
 * @param {Object} studioData - Studio data object
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - Returns true if sharing was successful
 */
export const shareStudio = async (studioData, options = {}) => {
  const shareData = createStudioShareData(studioData, options);
  if (!shareData) {
    console.error('shareStudio: Invalid studio data');
    return false;
  }
  
  return await shareContent(shareData, options);
};

/**
 * Share the current page URL
 * @param {Object} options - Custom options
 * @param {string} options.title - Custom title (optional)
 * @param {string} options.text - Custom text (optional)
 * @returns {Promise<boolean>} - Returns true if sharing was successful
 */

/**
 * Share just the current page URL with minimal content
 * @param {Object} options - Custom options
 * @returns {Promise<boolean>} - Returns true if sharing was successful
 */
export const shareUrl = async (options = {}) => {
  const url = window?.location?.href;
  
  // For Web Share API, we need at least title or text, so use minimal title
  if (navigator.share) {
    try {
      await navigator.share({
        title: document.title || "Link",
        url: url
      });
      return true;
    } catch (error) {
      return false;
    }
  } else {
    // For fallback, just copy the URL
    try {
      await navigator.clipboard.writeText(url);
      if (options.showAlert !== false) {
        notifySuccessToast('Link in die Zwischenablage kopiert!', 'Link kopiert');
      }
      return true;
    } catch (error) {
      prompt('Copy this link to share:', url);
      return true;
    }
  }
};
